import React, { useState, useRef, useCallback } from 'react';
import { Download, BarChart3, Upload, Edit3, Trash2, Plus, X } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ScatterChart, Scatter } from 'recharts';

const ChartDataEditor = () => {
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [chartType, setChartType] = useState('bar');
  const [chartData, setChartData] = useState([
    { name: 'Jan', value: 23, value2: 45 },
    { name: 'Fév', value: 45, value2: 30 },
    { name: 'Mar', value: 32, value2: 55 },
    { name: 'Avr', value: 67, value2: 40 },
    { name: 'Mai', value: 89, value2: 65 }
  ]);

  const [chartConfig, setChartConfig] = useState({
    title: 'Mon Graphique',
    showGrid: true,
    showGridHorizontal: true,
    showGridVertical: true,
    showLegend: true,
    showValues: true,
    colors: ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'],
    width: 600,
    height: 400
  });

  const [editingData, setEditingData] = useState(false);
  const [dataColumns, setDataColumns] = useState(['name', 'value', 'value2']);

  // Validation des données
  const validateValue = useCallback((value, isNumeric = true) => {
    if (!isNumeric) return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }, []);

  // Fonctions de gestion des données
  const addDataRow = () => {
    const newRow = {};
    dataColumns.forEach(col => {
      newRow[col] = col === 'name' ? `Item ${chartData.length + 1}` : 0;
    });
    setChartData([...chartData, newRow]);
  };

  const addDataColumn = () => {
    const newColName = `value${dataColumns.filter(col => col.startsWith('value')).length + 1}`;
    setDataColumns([...dataColumns, newColName]);
    setChartData(chartData.map(row => ({ ...row, [newColName]: 0 })));
  };

  const updateDataCell = (rowIndex, column, value) => {
    const newData = [...chartData];
    newData[rowIndex] = { 
      ...newData[rowIndex], 
      [column]: column === 'name' ? value : validateValue(value)
    };
    setChartData(newData);
  };

  const deleteDataRow = (index) => {
    if (chartData.length <= 1) {
      alert("Vous devez conserver au moins une ligne de données");
      return;
    }
    setChartData(chartData.filter((_, i) => i !== index));
  };

  const deleteDataColumn = (columnName) => {
    if (dataColumns.length <= 2) {
      alert("Vous devez conserver au moins deux colonnes");
      return;
    }
    
    const newColumns = dataColumns.filter(col => col !== columnName);
    setDataColumns(newColumns);
    
    const newData = chartData.map(row => {
      const newRow = { ...row };
      delete newRow[columnName];
      return newRow;
    });
    setChartData(newData);
  };

  const renameColumn = (oldName, newName) => {
    if (!newName.trim() || oldName === newName) return;
    
    if (dataColumns.includes(newName)) {
      alert('Ce nom de colonne existe déjà !');
      return;
    }
    
    const newColumns = dataColumns.map(col => col === oldName ? newName : col);
    setDataColumns(newColumns);
    
    const newData = chartData.map(row => {
      const newRow = { ...row };
      if (oldName in newRow) {
        newRow[newName] = newRow[oldName];
        delete newRow[oldName];
      }
      return newRow;
    });
    setChartData(newData);
  };

  // Import CSV amélioré
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('Veuillez sélectionner un fichier CSV');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert('Le fichier CSV doit contenir au moins un en-tête et une ligne de données');
          return;
        }
        
        const headers = lines[0].split(/[,;|\t]/).map(h => h.trim().replace(/^["']|["']$/g, ''));
        const data = lines.slice(1).map(line => {
          const values = line.split(/[,;|\t]/).map(v => v.trim().replace(/^["']|["']$/g, ''));
          const row = {};
          headers.forEach((header, index) => {
            const value = values[index] || '';
            row[header] = isNaN(value) || value === '' ? value : parseFloat(value);
          });
          return row;
        });
        
        setDataColumns(headers);
        setChartData(data);
        alert('Fichier CSV importé avec succès !');
      } catch (error) {
        alert('Erreur lors de l\'import du fichier CSV');
        console.error(error);
      }
    };
    
    reader.onerror = () => {
      alert('Erreur lors de la lecture du fichier');
    };
    
    reader.readAsText(file);
    event.target.value = ''; // Reset pour permettre de réimporter le même fichier
  };

  // Export CSV
  const exportCSV = () => {
    const headers = dataColumns.join(',');
    const rows = chartData.map(row => 
      dataColumns.map(col => row[col] || '').join(',')
    ).join('\n');
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chartConfig.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export SVG amélioré
  const exportSVG = () => {
    const svgWidth = chartConfig.width + 100;
    const svgHeight = chartConfig.height + 150;
    
    const valueColumns = dataColumns.filter(col => col !== 'name');
    
    let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <style>
      .chart-title { font-family: Arial, sans-serif; font-size: 18px; font-weight: bold; fill: #333; }
      .axis-label { font-family: Arial, sans-serif; font-size: 12px; fill: #666; }
      .axis-line { stroke: #666; stroke-width: 1; }
      .grid-line { stroke: #e0e0e0; stroke-width: 1; stroke-dasharray: 3,3; }
      .value-label { font-family: Arial, sans-serif; font-size: 11px; fill: white; font-weight: bold; }
      .legend-text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
    </style>
    ${valueColumns.map((col, index) => `
    <linearGradient id="color-${col.replace(/[^a-zA-Z0-9]/g, '')}" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="${chartConfig.colors[index % chartConfig.colors.length]}" />
      <stop offset="100%" stop-color="${chartConfig.colors[index % chartConfig.colors.length]}" />
    </linearGradient>`).join('')}
  </defs>
  
  <rect width="100%" height="100%" fill="white"/>
  
  <!-- Titre -->
  <text x="${svgWidth / 2}" y="30" text-anchor="middle" class="chart-title">${chartConfig.title}</text>
  
  <!-- Zone de graphique -->
  <g transform="translate(50, 60)">`;

    // Génération du contenu selon le type de graphique
    if (chartType === 'bar' || chartType === 'horizontal-bar') {
      svgContent += generateBarChartSVG(chartType === 'horizontal-bar');
    } else if (chartType === 'stacked-bar') {
      svgContent += generateStackedBarChartSVG();
    } else if (chartType === 'line') {
      svgContent += generateLineChartSVG();
    } else if (chartType === 'pie' || chartType === 'donut') {
      svgContent += generatePieChartSVG(chartType === 'donut');
    } else if (chartType === 'area') {
      svgContent += generateAreaChartSVG();
    }

    svgContent += `
  </g>
</svg>`;

    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chartConfig.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Fonctions de génération SVG pour chaque type
  const generateBarChartSVG = (isHorizontal = false) => {
    const valueColumns = dataColumns.filter(col => col !== 'name');
    const maxValue = Math.max(...chartData.flatMap(row => 
      valueColumns.map(col => row[col] || 0)
    ));
    
    const chartW = chartConfig.width - 60;
    const chartH = chartConfig.height - 60;
    
    let svg = `
    <!-- Axes -->
    <line x1="0" y1="0" x2="0" y2="${chartH}" class="axis-line"/>
    <line x1="0" y1="${chartH}" x2="${chartW}" y2="${chartH}" class="axis-line"/>`;
    
    // Grille et valeurs Y
    const gridSteps = 5;
    for (let i = 0; i <= gridSteps; i++) {
      const y = chartH - (chartH / gridSteps) * i;
      const value = Math.round((maxValue / gridSteps) * i);
      
      // Grille
      if (chartConfig.showGrid && i > 0 && i < gridSteps) {
        svg += `
    <line x1="0" y1="${y}" x2="${chartW}" y2="${y}" class="grid-line"/>`;
      }
      
      // Valeurs Y
      svg += `
    <text x="-10" y="${y + 4}" text-anchor="end" font-size="10" fill="#666">${value}</text>
    <line x1="-5" y1="${y}" x2="0" y2="${y}" stroke="#666" stroke-width="1"/>`;
    }
    
    // Barres et labels
    const barWidth = chartW / (chartData.length * valueColumns.length);
    
    chartData.forEach((row, rowIndex) => {
      valueColumns.forEach((col, colIndex) => {
        const value = row[col] || 0;
        
        // Ne pas afficher les barres avec valeur 0
        if (value > 0) {
          const barHeight = (value / maxValue) * chartH;
          const x = (rowIndex * valueColumns.length + colIndex) * barWidth + 5;
          const y = chartH - barHeight;
          
          svg += `
    <rect x="${x}" y="${y}" width="${barWidth - 2}" height="${barHeight}" 
          fill="url(#color-${col.replace(/[^a-zA-Z0-9]/g, '')})" />`;
          
          if (chartConfig.showValues) {
            svg += `
    <text x="${x + (barWidth - 2)/2}" y="${y - 5}" text-anchor="middle" class="value-label" fill="#333">${value}</text>`;
          }
        }
      });
      
      svg += `
    <text x="${(rowIndex + 0.5) * barWidth * valueColumns.length}" y="${chartH + 20}" 
          text-anchor="middle" class="axis-label">${row.name}</text>`;
    });
    
    // Légende
    if (chartConfig.showLegend && valueColumns.length > 1) {
      const legendY = chartH + 60;
      valueColumns.forEach((col, index) => {
        const x = index * 120;
        svg += `
    <rect x="${x}" y="${legendY}" width="15" height="15" 
          fill="url(#color-${col.replace(/[^a-zA-Z0-9]/g, '')})" />
    <text x="${x + 20}" y="${legendY + 12}" class="legend-text">${col}</text>`;
      });
    }
    
    return svg;
  };

  const generateStackedBarChartSVG = () => {
    const valueColumns = dataColumns.filter(col => col !== 'name');
    const totals = chartData.map(row => 
      valueColumns.reduce((sum, col) => sum + (row[col] || 0), 0)
    );
    const maxTotal = Math.max(...totals);
    
    const chartW = chartConfig.width - 60;
    const chartH = chartConfig.height - 60;
    const barWidth = chartW / chartData.length;
    
    let svg = `
    <!-- Axes -->
    <line x1="0" y1="0" x2="0" y2="${chartH}" class="axis-line"/>
    <line x1="0" y1="${chartH}" x2="${chartW}" y2="${chartH}" class="axis-line"/>`;
    
    // Grille et valeurs Y
    const gridSteps = 5;
    for (let i = 0; i <= gridSteps; i++) {
      const y = chartH - (chartH / gridSteps) * i;
      const value = Math.round((maxTotal / gridSteps) * i);
      
      // Grille
      if (chartConfig.showGrid && i > 0 && i < gridSteps) {
        svg += `
    <line x1="0" y1="${y}" x2="${chartW}" y2="${y}" class="grid-line"/>`;
      }
      
      // Valeurs Y
      svg += `
    <text x="-10" y="${y + 4}" text-anchor="end" font-size="10" fill="#666">${value}</text>
    <line x1="-5" y1="${y}" x2="0" y2="${y}" stroke="#666" stroke-width="1"/>`;
    }
    
    // Barres empilées
    chartData.forEach((row, rowIndex) => {
      let currentY = chartH;
      const x = rowIndex * barWidth + 10;
      const width = barWidth - 20;
      
      valueColumns.forEach((col, colIndex) => {
        const value = row[col] || 0;
        
        // Ne pas afficher les segments avec valeur 0
        if (value > 0) {
          const segmentHeight = (value / maxTotal) * chartH;
          currentY -= segmentHeight;
          
          svg += `
    <rect x="${x}" y="${currentY}" width="${width}" height="${segmentHeight}" 
          fill="url(#color-${col.replace(/[^a-zA-Z0-9]/g, '')})" />`;
          
          if (chartConfig.showValues && segmentHeight > 20) {
            svg += `
    <text x="${x + width/2}" y="${currentY + segmentHeight/2 + 4}" 
          text-anchor="middle" class="value-label">${value}</text>`;
          }
        }
      });
      
      svg += `
    <text x="${x + width/2}" y="${chartH + 20}" text-anchor="middle" class="axis-label">${row.name}</text>`;
    });
    
    // Légende
    if (chartConfig.showLegend && valueColumns.length > 1) {
      const legendY = chartH + 60;
      valueColumns.forEach((col, index) => {
        const x = index * 120;
        svg += `
    <rect x="${x}" y="${legendY}" width="15" height="15" 
          fill="url(#color-${col.replace(/[^a-zA-Z0-9]/g, '')})" />
    <text x="${x + 20}" y="${legendY + 12}" class="legend-text">${col}</text>`;
      });
    }
    
    return svg;
  };

  const generateLineChartSVG = () => {
    const valueColumns = dataColumns.filter(col => col !== 'name');
    const maxValue = Math.max(...chartData.flatMap(row => 
      valueColumns.map(col => row[col] || 0)
    ));
    
    const chartW = chartConfig.width - 60;
    const chartH = chartConfig.height - 60;
    const pointSpacing = chartW / (chartData.length - 1 || 1);
    
    let svg = `
    <!-- Axes -->
    <line x1="0" y1="0" x2="0" y2="${chartH}" class="axis-line"/>
    <line x1="0" y1="${chartH}" x2="${chartW}" y2="${chartH}" class="axis-line"/>`;
    
    // Grille et valeurs Y
    const gridSteps = 5;
    for (let i = 0; i <= gridSteps; i++) {
      const y = chartH - (chartH / gridSteps) * i;
      const value = Math.round((maxValue / gridSteps) * i);
      
      // Grille
      if (chartConfig.showGrid && i > 0 && i < gridSteps) {
        svg += `
    <line x1="0" y1="${y}" x2="${chartW}" y2="${y}" class="grid-line"/>`;
      }
      
      // Valeurs Y
      svg += `
    <text x="-10" y="${y + 4}" text-anchor="end" font-size="10" fill="#666">${value}</text>
    <line x1="-5" y1="${y}" x2="0" y2="${y}" stroke="#666" stroke-width="1"/>`;
    }
    
    // Lignes
    valueColumns.forEach((col, colIndex) => {
      // Filtrer les points avec valeur 0 pour créer une ligne continue seulement entre les valeurs non-nulles
      const validPoints = [];
      chartData.forEach((row, index) => {
        const value = row[col] || 0;
        if (value > 0) {
          const x = index * pointSpacing;
          const y = chartH - (value / maxValue) * chartH;
          validPoints.push({ x, y, value });
        }
      });
      
      // Dessiner la ligne seulement s'il y a des points valides
      if (validPoints.length > 0) {
        const points = validPoints.map(p => `${p.x},${p.y}`).join(' ');
        
        svg += `
    <polyline points="${points}" fill="none" 
              stroke="${chartConfig.colors[colIndex % chartConfig.colors.length]}" 
              stroke-width="2" />`;
        
        // Points
        validPoints.forEach(point => {
          svg += `
    <circle cx="${point.x}" cy="${point.y}" r="4" 
            fill="${chartConfig.colors[colIndex % chartConfig.colors.length]}" />`;
          
          if (chartConfig.showValues) {
            svg += `
    <text x="${point.x}" y="${point.y - 10}" text-anchor="middle" font-size="10" fill="#333">${point.value}</text>`;
          }
        });
      }
    });
    
    // Labels X
    chartData.forEach((row, index) => {
      svg += `
    <text x="${index * pointSpacing}" y="${chartH + 20}" 
          text-anchor="middle" class="axis-label">${row.name}</text>`;
    });
    
    // Légende
    if (chartConfig.showLegend && valueColumns.length > 1) {
      const legendY = chartH + 60;
      valueColumns.forEach((col, index) => {
        const x = index * 120;
        svg += `
    <rect x="${x}" y="${legendY}" width="15" height="15" 
          fill="${chartConfig.colors[index % chartConfig.colors.length]}" />
    <text x="${x + 20}" y="${legendY + 12}" class="legend-text">${col}</text>`;
      });
    }
    
    return svg;
  };

  const generatePieChartSVG = (isDonut = false) => {
    const centerX = chartConfig.width / 2;
    const centerY = chartConfig.height / 2;
    const radius = Math.min(chartConfig.width, chartConfig.height) / 3;
    const innerRadius = isDonut ? radius / 2 : 0;
    
    const total = chartData.reduce((sum, row) => sum + (row.value || 0), 0);
    let currentAngle = -Math.PI / 2;
    
    let svg = '';
    
    chartData.forEach((row, index) => {
      const value = row.value || 0;
      const angle = (value / total) * 2 * Math.PI;
      const endAngle = currentAngle + angle;
      
      const x1 = centerX + Math.cos(currentAngle) * radius;
      const y1 = centerY + Math.sin(currentAngle) * radius;
      const x2 = centerX + Math.cos(endAngle) * radius;
      const y2 = centerY + Math.sin(endAngle) * radius;
      
      const x1Inner = centerX + Math.cos(currentAngle) * innerRadius;
      const y1Inner = centerY + Math.sin(currentAngle) * innerRadius;
      const x2Inner = centerX + Math.cos(endAngle) * innerRadius;
      const y2Inner = centerY + Math.sin(endAngle) * innerRadius;
      
      const largeArc = angle > Math.PI ? 1 : 0;
      
      let path;
      if (isDonut) {
        path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} 
                L ${x2Inner} ${y2Inner} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1Inner} ${y1Inner} Z`;
      } else {
        path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      }
      
      svg += `
    <path d="${path}" fill="${chartConfig.colors[index % chartConfig.colors.length]}" 
          stroke="white" stroke-width="2" />`;
      
      // Label
      if (chartConfig.showValues && value > 0) {
        const labelAngle = currentAngle + angle / 2;
        const labelRadius = isDonut ? (radius + innerRadius) / 2 : radius * 0.7;
        const labelX = centerX + Math.cos(labelAngle) * labelRadius;
        const labelY = centerY + Math.sin(labelAngle) * labelRadius;
        
        const percentage = Math.round((value / total) * 100);
        svg += `
    <text x="${labelX}" y="${labelY}" text-anchor="middle" 
          dominant-baseline="central" class="value-label">${percentage}%</text>`;
      }
      
      currentAngle = endAngle;
    });
    
    // Légende
    if (chartConfig.showLegend) {
      const legendY = chartConfig.height - 30;
      chartData.forEach((row, index) => {
        const x = 20 + (index % 3) * 150;
        const y = legendY + Math.floor(index / 3) * 25;
        svg += `
    <rect x="${x}" y="${y}" width="15" height="15" 
          fill="${chartConfig.colors[index % chartConfig.colors.length]}" />
    <text x="${x + 20}" y="${y + 12}" class="legend-text">${row.name}: ${row.value || 0}</text>`;
      });
    }
    
    return svg;
  };

  const generateAreaChartSVG = () => {
    const valueColumns = dataColumns.filter(col => col !== 'name');
    const maxValue = Math.max(...chartData.flatMap(row => 
      valueColumns.map(col => row[col] || 0)
    ));
    
    const chartW = chartConfig.width - 60;
    const chartH = chartConfig.height - 60;
    const pointSpacing = chartW / (chartData.length - 1 || 1);
    
    let svg = `
    <!-- Axes -->
    <line x1="0" y1="0" x2="0" y2="${chartH}" class="axis-line"/>
    <line x1="0" y1="${chartH}" x2="${chartW}" y2="${chartH}" class="axis-line"/>`;
    
    // Grille et valeurs Y
    const gridSteps = 5;
    for (let i = 0; i <= gridSteps; i++) {
      const y = chartH - (chartH / gridSteps) * i;
      const value = Math.round((maxValue / gridSteps) * i);
      
      // Grille
      if (chartConfig.showGrid && i > 0 && i < gridSteps) {
        svg += `
    <line x1="0" y1="${y}" x2="${chartW}" y2="${y}" class="grid-line"/>`;
      }
      
      // Valeurs Y
      svg += `
    <text x="-10" y="${y + 4}" text-anchor="end" font-size="10" fill="#666">${value}</text>
    <line x1="-5" y1="${y}" x2="0" y2="${y}" stroke="#666" stroke-width="1"/>`;
    }
    
    // Aires
    valueColumns.forEach((col, colIndex) => {
      const points = chartData.map((row, index) => {
        const value = row[col] || 0;
        const x = index * pointSpacing;
        const y = value > 0 ? chartH - (value / maxValue) * chartH : chartH;
        return `${x},${y}`;
      }).join(' ');
      
      const areaPath = `${points} ${chartW},${chartH} 0,${chartH}`;
      
      svg += `
    <polygon points="${areaPath}" 
             fill="${chartConfig.colors[colIndex % chartConfig.colors.length]}" 
             fill-opacity="0.5" />
    <polyline points="${points}" fill="none" 
              stroke="${chartConfig.colors[colIndex % chartConfig.colors.length]}" 
              stroke-width="2" />`;
      
      // Points avec valeurs > 0
      chartData.forEach((row, index) => {
        const value = row[col] || 0;
        if (value > 0) {
          const x = index * pointSpacing;
          const y = chartH - (value / maxValue) * chartH;
          
          svg += `
    <circle cx="${x}" cy="${y}" r="3" 
            fill="${chartConfig.colors[colIndex % chartConfig.colors.length]}" />`;
          
          if (chartConfig.showValues) {
            svg += `
    <text x="${x}" y="${y - 10}" text-anchor="middle" font-size="10" fill="#333">${value}</text>`;
          }
        }
      });
    });
    
    // Labels X
    chartData.forEach((row, index) => {
      svg += `
    <text x="${index * pointSpacing}" y="${chartH + 20}" 
          text-anchor="middle" class="axis-label">${row.name}</text>`;
    });
    
    // Légende
    if (chartConfig.showLegend && valueColumns.length > 1) {
      const legendY = chartH + 60;
      valueColumns.forEach((col, index) => {
        const x = index * 120;
        svg += `
    <rect x="${x}" y="${legendY}" width="15" height="15" 
          fill="${chartConfig.colors[index % chartConfig.colors.length]}" />
    <text x="${x + 20}" y="${legendY + 12}" class="legend-text">${col}</text>`;
      });
    }
    
    return svg;
  };

  // Rendu du graphique
  const renderChart = () => {
    const commonProps = {
      width: chartConfig.width,
      height: chartConfig.height,
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3" 
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {dataColumns.filter(col => col !== 'name').map((col, index) => (
              <Bar 
                key={col} 
                dataKey={col} 
                fill={chartConfig.colors[index % chartConfig.colors.length]}
                label={chartConfig.showValues ? { position: 'top', fontSize: 12, fill: '#333' } : false}
              />
            ))}
          </BarChart>
        );
        
      case 'stacked-bar':
        return (
          <BarChart {...commonProps}>
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3"
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {dataColumns.filter(col => col !== 'name').map((col, index) => (
              <Bar 
                key={col} 
                dataKey={col} 
                stackId="stack" 
                fill={chartConfig.colors[index % chartConfig.colors.length]}
              >
                {chartConfig.showValues && (
                  <LabelList 
                    dataKey={col}
                    position="center" 
                    fontSize={11} 
                    fill="white" 
                    fontWeight="bold"
                  />
                )}
              </Bar>
            ))}
          </BarChart>
        );
        
      case 'line':
        return (
          <LineChart {...commonProps}>
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3"
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {dataColumns.filter(col => col !== 'name').map((col, index) => (
              <Line 
                key={col} 
                type="monotone" 
                dataKey={col} 
                stroke={chartConfig.colors[index % chartConfig.colors.length]} 
                strokeWidth={2}
                dot={chartConfig.showValues}
                label={chartConfig.showValues ? { position: 'top', fontSize: 10, fill: '#333' } : false}
              />
            ))}
          </LineChart>
        );
        
      case 'pie':
        return (
          <PieChart {...commonProps}>
            <Pie
              data={chartData}
              cx={chartConfig.width / 2}
              cy={chartConfig.height / 2}
              outerRadius={Math.min(chartConfig.width, chartConfig.height) / 3}
              fill="#8884d8"
              dataKey="value"
              label={chartConfig.showValues}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartConfig.colors[index % chartConfig.colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
          </PieChart>
        );
        
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3"
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {dataColumns.filter(col => col !== 'name').map((col, index) => (
              <Area 
                key={col} 
                type="monotone" 
                dataKey={col} 
                stackId="1" 
                stroke={chartConfig.colors[index % chartConfig.colors.length]} 
                fill={chartConfig.colors[index % chartConfig.colors.length]}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        );
        
      case 'horizontal-bar':
        return (
          <BarChart {...commonProps} layout="horizontal">
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3"
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {dataColumns.filter(col => col !== 'name').map((col, index) => (
              <Bar 
                key={col} 
                dataKey={col} 
                fill={chartConfig.colors[index % chartConfig.colors.length]}
                label={chartConfig.showValues ? { position: 'right', fontSize: 12, fill: '#333' } : false}
              />
            ))}
          </BarChart>
        );
        
      case 'donut':
        return (
          <PieChart {...commonProps}>
            <Pie
              data={chartData}
              cx={chartConfig.width / 2}
              cy={chartConfig.height / 2}
              innerRadius={Math.min(chartConfig.width, chartConfig.height) / 6}
              outerRadius={Math.min(chartConfig.width, chartConfig.height) / 3}
              fill="#8884d8"
              dataKey="value"
              label={chartConfig.showValues}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartConfig.colors[index % chartConfig.colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
          </PieChart>
        );
        
      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            {chartConfig.showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3"
                horizontal={chartConfig.showGridHorizontal}
                vertical={chartConfig.showGridVertical}
              />
            )}
            <XAxis 
              type="number" 
              dataKey={dataColumns.filter(col => col !== 'name')[0] || 'value'}
              name={dataColumns.filter(col => col !== 'name')[0] || 'X'}
            />
            <YAxis 
              type="number" 
              dataKey={dataColumns.filter(col => col !== 'name')[1] || 'value2'}
              name={dataColumns.filter(col => col !== 'name')[1] || 'Y'}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            {chartConfig.showLegend && <Legend />}
            <Scatter 
              name="Données" 
              data={chartData} 
              fill={chartConfig.colors[0]}
              label={chartConfig.showValues ? { dataKey: 'name' } : false}
            />
          </ScatterChart>
        );
        
      case 'radar':
        const radarData = dataColumns.filter(col => col !== 'name').map(col => ({
          subject: col,
          ...chartData.reduce((acc, row, index) => ({
            ...acc,
            [row.name]: row[col] || 0
          }), {})
        }));
        
        return (
          <RadarChart {...commonProps} data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <PolarRadiusAxis />
            <Tooltip />
            {chartConfig.showLegend && <Legend />}
            {chartData.map((row, index) => (
              <Radar
                key={row.name}
                name={row.name}
                dataKey={row.name}
                stroke={chartConfig.colors[index % chartConfig.colors.length]}
                fill={chartConfig.colors[index % chartConfig.colors.length]}
                fillOpacity={0.3}
              />
            ))}
          </RadarChart>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Barre d'outils */}
      <div className="w-16 bg-gray-800 text-white flex flex-col items-center py-4 space-y-2">
        <div className="p-2 rounded bg-blue-600" title="Mode Graphique">
          <BarChart3 size={20} />
        </div>

        <div className="border-t border-gray-600 w-10 my-2"></div>

        <button
          onClick={exportSVG}
          className="p-2 rounded bg-green-600 hover:bg-green-500 transition-colors"
          title="Exporter SVG"
        >
          <Download size={20} />
        </button>
        
        <button
          onClick={exportCSV}
          className="p-2 rounded bg-purple-600 hover:bg-purple-500 transition-colors"
          title="Exporter CSV"
        >
          <Upload size={20} className="rotate-180" />
        </button>
      </div>

      {/* Zone principale */}
      <div className="flex-1 flex">
        {/* Canevas */}
        <div className="flex-1 p-4 overflow-auto">
          <div className="bg-white rounded-lg shadow-lg p-4">
            <div className="mb-4 flex space-x-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de graphique</label>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bar">Barres</option>
                  <option value="stacked-bar">Barres empilées</option>
                  <option value="line">Courbes</option>
                  <option value="pie">Secteurs</option>
                  <option value="area">Aires</option>
                  <option value="horizontal-bar">Barres horizontales</option>
                  <option value="donut">Donut</option>
                  <option value="scatter">Nuage de points</option>
                  <option value="radar">Radar</option>
                </select>
              </div>
            </div>
            
            <div className="border border-gray-300 inline-block bg-white p-4 rounded">
              <svg
                ref={svgRef}
                width={chartConfig.width + 100}
                height={chartConfig.height + 100}
                viewBox={`0 0 ${chartConfig.width + 100} ${chartConfig.height + 100}`}
                className="bg-white"
              >
                <text x={(chartConfig.width + 100) / 2} y={30} textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">
                  {chartConfig.title}
                </text>

                <foreignObject x={50} y={50} width={chartConfig.width} height={chartConfig.height}>
                  <div style={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {renderChart()}
                    </ResponsiveContainer>
                  </div>
                </foreignObject>
              </svg>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="w-80 bg-white border-l border-gray-300 flex flex-col overflow-hidden">
          <div className="flex-1 p-4 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Configuration du Graphique v0.1</h3>
            
            {/* Configuration générale */}
            <div className="mb-6 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={chartConfig.title}
                  onChange={(e) => setChartConfig(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Largeur</label>
                  <input
                    type="number"
                    value={chartConfig.width}
                    onChange={(e) => setChartConfig(prev => ({ ...prev, width: parseInt(e.target.value) || 600 }))}
                    min="200"
                    max="1200"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hauteur</label>
                  <input
                    type="number"
                    value={chartConfig.height}
                    onChange={(e) => setChartConfig(prev => ({ ...prev, height: parseInt(e.target.value) || 400 }))}
                    min="200"
                    max="800"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartConfig.showLegend}
                      onChange={(e) => setChartConfig(prev => ({ ...prev, showLegend: e.target.checked }))}
                      className="mr-2 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Légende</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartConfig.showValues}
                      onChange={(e) => setChartConfig(prev => ({ ...prev, showValues: e.target.checked }))}
                      className="mr-2 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Valeurs</span>
                  </label>
                </div>
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-700">Grille :</h5>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartConfig.showGridHorizontal}
                      onChange={(e) => setChartConfig(prev => ({ 
                        ...prev, 
                        showGridHorizontal: e.target.checked,
                        showGrid: e.target.checked || prev.showGridVertical 
                      }))}
                      className="mr-2 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Horizontale</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartConfig.showGridVertical}
                      onChange={(e) => setChartConfig(prev => ({ 
                        ...prev, 
                        showGridVertical: e.target.checked,
                        showGrid: e.target.checked || prev.showGridHorizontal 
                      }))}
                      className="mr-2 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Verticale</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Données */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Données</h4>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
                    title="Importer CSV"
                  >
                    <Upload size={16} className="mr-1" />
                    CSV
                  </button>
                  <button
                    onClick={() => setEditingData(!editingData)}
                    className={`px-3 py-1 text-white text-sm rounded transition-colors flex items-center ${
                      editingData ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                    title="Éditer données"
                  >
                    <Edit3 size={16} className="mr-1" />
                    {editingData ? 'Terminé' : 'Éditer'}
                  </button>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />

              {editingData ? (
                <div className="border border-gray-300 rounded overflow-hidden">
                  <div className="bg-gray-50 p-2 border-b">
                    <div className="flex space-x-2">
                      <button
                        onClick={addDataRow}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center"
                      >
                        <Plus size={14} className="mr-1" /> Ligne
                      </button>
                      <button
                        onClick={addDataColumn}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <Plus size={14} className="mr-1" /> Colonne
                      </button>
                    </div>
                  </div>
                  <div className="overflow-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          {dataColumns.map(col => (
                            <th key={col} className="p-2 border-r text-left font-medium">
                              <div className="flex items-center justify-between">
                                <span className="text-xs">{col}</span>
                                {col !== 'name' && dataColumns.length > 2 && (
                                  <button
                                    onClick={() => deleteDataColumn(col)}
                                    className="ml-1 text-red-600 hover:text-red-800 text-xs"
                                    title="Supprimer"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </th>
                          ))}
                          <th className="p-2 text-center">
                            <span className="text-xs">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {chartData.map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-t hover:bg-gray-50">
                            {dataColumns.map(col => (
                              <td key={col} className="p-1 border-r">
                                <input
                                  type={col === 'name' ? 'text' : 'number'}
                                  value={row[col] || ''}
                                  onChange={(e) => updateDataCell(rowIndex, col, e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </td>
                            ))}
                            <td className="p-1 text-center">
                              <button
                                onClick={() => deleteDataRow(rowIndex)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="text-sm text-gray-600 mb-2">
                    📊 {chartData.length} lignes, {dataColumns.length} colonnes
                  </div>
                  <div className="text-xs text-gray-500">
                    Cliquez sur "Éditer" pour modifier vos données
                  </div>
                </div>
              )}
            </div>

            {/* Couleurs */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">Couleurs</h4>
              <div className="grid grid-cols-5 gap-2">
                {chartConfig.colors.map((color, index) => (
                  <div key={index} className="relative group">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => {
                        const newColors = [...chartConfig.colors];
                        newColors[index] = e.target.value;
                        setChartConfig(prev => ({ ...prev, colors: newColors }));
                      }}
                      className="w-full h-10 border border-gray-300 rounded cursor-pointer hover:border-gray-400 transition-colors"
                      title={`Couleur ${index + 1}`}
                    />
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {color}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">💡 Guide d'utilisation</h4>
              <div className="text-xs text-blue-700 space-y-1">
                <p>• <strong>Import CSV :</strong> Supporte les séparateurs , ; | et tab</p>
                <p>• <strong>Export SVG :</strong> Optimisé pour InDesign/Illustrator</p>
                <p>• <strong>Export CSV :</strong> Sauvegardez vos données modifiées</p>
                <p>• <strong>Grille :</strong> Contrôle indépendant H/V pour plus de flexibilité</p>
                <p>• <strong>Validation :</strong> Les valeurs numériques sont automatiquement vérifiées</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDataEditor;